// import jsPDF from 'jspdf';
import { supabase, supabaseUrl, supabaseKey } from './supabaseMock';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Answer {
    id: number;
    call_id: number;
    question_id: number;
    text: string | null;
    picture: string | null;
    signature: string | null;
    number: string | null;
    choices: any | null;
    yes_no: boolean | null;
    datetime: string | null;
    value?: any;
    picture_url?: string | null;
    updated_at?: string | null;
}

interface Question {
    QuestionID: number;
    Caption: { DefaultCaption: string };
    Type: number;
    Required?: boolean;
    ReadOnly?: boolean;
}

interface Form {
    FormID: number;
    Caption: { DefaultCaption: string };
    Questions: Question[];
}

interface Questionnaire {
    id: number;
    questionnaire_id: number;
    project_id: number;
    forms: string;
}

interface CallData {
    call_id?: number;
    actual_start?: string;
    position?: { lat: number; lng: number };
    [key: string]: any;
}

export interface SingleStoreExportData {
    storeName: string;
    projectName: string;
    callData: CallData;
    questionnaire: Questionnaire | null;
    answers: Answer[];
}

export interface ProjectExportData {
    projectName: string;
    stores: SingleStoreExportData[];
}

// ─── Constants ────────────────────────────────────────────────────────────

const THUMBNAIL_BASE_URL = 'https://images.teamhaven.com/size/400';
// const PICTURE_BASE_URL = 'https://test.visibleworx.com/testproxy/one/pictures';
// const SIGNATURE_BASE_URL = 'https://test.visibleworx.com/testproxy/one/signatures';
// const COLOR_SLATE = [71, 87, 99] as const;
// const COLOR_GREEN = [150, 195, 109] as const;
// const COLOR_BORDER = [226, 231, 240] as const;

// ─── Remote PDF Logic ────────────────────────────────────────────────────────

async function generateRemotePDF(html: string, fileName: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(
        `${supabaseUrl}/functions/v1/generate-report-pdf`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
                'apikey': supabaseKey,
            },
            body: JSON.stringify({ html, projectName: fileName }),
        }
    );

    if (!response.ok) {
        throw new Error('Remote PDF generation failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function buildStoreHTML(storeData: SingleStoreExportData): string {
    const { storeName, projectName, callData, answers } = storeData;
    return `
    <html>
    <head>
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap" rel="stylesheet">
        <style>
            body { font-family: 'Montserrat', sans-serif; padding: 40px; color: #2a3339; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #96c56c; padding-bottom: 20px; margin-bottom: 30px; }
            .store-name { font-size: 24px; font-weight: 700; margin-bottom: 5px; }
            .meta { font-size: 12px; color: #717d89; }
            .section-title { font-size: 18px; font-weight: 700; margin: 30px 0 15px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; }
            .q-row { display: flex; border-bottom: 1px solid #f1f5f9; padding: 10px 0; font-size: 13px; }
            .q-text { flex: 2; font-weight: 700; }
            .a-text { flex: 1; color: #475763; }
            .photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 20px; }
            .photo-card { border: 1px solid #e2e8f0; padding: 5px; border-radius: 4px; }
            .photo-card img { width: 100%; border-radius: 2px; }
            .photo-caption { font-size: 10px; margin-top: 5px; text-align: center; color: #717d89; }
        </style>
    </head>
    <body>
        <div class="header">
            <div>
                <div class="store-name">${storeName}</div>
                <div class="meta">Project: ${projectName}</div>
            </div>
            <div class="meta" style="text-align: right">
                Date: ${new Date(callData?.actual_start || Date.now()).toLocaleDateString('en-GB')}
            </div>
        </div>

        <div class="section-title">Questionnaire Results</div>
        ${answers.map(a => `
            <div class="q-row">
                <div class="q-text">Question ID: ${a.question_id}</div>
                <div class="a-text">${a.text || a.value || 'N/A'}</div>
            </div>
        `).join('')}

        <div class="section-title">Visual Evidence</div>
        <div class="photo-grid">
            ${answers.filter(a => a.picture).map(a => `
                <div class="photo-card">
                    <img src="https://images.teamhaven.com/size/400/${a.picture}.jpg" />
                    <div class="photo-caption">Question ID: ${a.question_id}</div>
                </div>
            `).join('')}
        </div>
    </body>
    </html>
    `;
}

// ─── Public API ─────────────────────────────────────────────────────────────

export async function exportSingleStorePDF(storeData: SingleStoreExportData): Promise<void> {
    const html = buildStoreHTML(storeData);
    await generateRemotePDF(html, `${storeData.storeName}_Report`);
}

export async function exportProjectPDF(
    projectData: ProjectExportData,
    onProgress?: (current: number, total: number) => void
): Promise<void> {
    for (let i = 0; i < projectData.stores.length; i++) {
        if (onProgress) onProgress(i + 1, projectData.stores.length);
        const store = projectData.stores[i];
        const html = buildStoreHTML(store);
        await generateRemotePDF(html, `${store.storeName}_Report`);
    }
}

/* 
   LEGACY BLOCK (Requires jsPDF) - Preserved for reference
   
   function parseForms(formsString: string): Form[] { ... }
   function buildAnswerMap(answers: Answer[]): Map<number, Answer> { ... }
   async function loadImage(url: string, retries = 2): Promise<{ data: string; width: number; height: number }> { ... }
   function addHeader(doc: any, projectName: string, showBack = false): void { ... }
   async function addPhotoGrid(doc: any, entries: any[], startY: number, projectName: string): Promise<void> { ... }
   async function renderStorePage(doc: any, storeData: any): Promise<void> { ... }
*/